import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api-client';
import { canSearchPatientUhid } from '../../lib/patient-uhid-search';
import {
  createDialysisAdmission,
  fetchDialysisAdmission,
  fetchDialysisAdmissions,
  fetchDialysisLookups,
  fetchDialysisPrescription,
  saveDialysisPrescription,
  updateDialysisAdmission,
} from './dialysis.api';
import type { DialysisListFilters, UpdateDialysisAdmissionPayload, UpsertDialysisPrescriptionPayload } from './types';

export const useDialysisAdmissions = (filters: DialysisListFilters) => useQuery({ queryKey: ['dialysis', 'admissions', filters], queryFn: () => fetchDialysisAdmissions(filters) });
export const useDialysisAdmission = (id?: string) => useQuery({ queryKey: ['dialysis', 'admission', id], queryFn: () => fetchDialysisAdmission(id!), enabled: Boolean(id) });
export const useDialysisLookup = (type: string, search = '', extra: { state?: string; ward?: string } = {}) => useQuery({
  queryKey: ['dialysis', 'lookup', type, search, extra],
  queryFn: () => fetchDialysisLookups(type, search, extra),
  enabled: type !== 'patients' || canSearchPatientUhid(search),
  ...(type === 'patients' ? { staleTime: 0, refetchOnMount: 'always' as const } : { staleTime: 60_000 }),
});

export const useCreateDialysisAdmission = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: createDialysisAdmission, onSuccess: () => void client.invalidateQueries({ queryKey: ['dialysis'] }) });
};

export const useUpdateDialysisAdmission = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDialysisAdmissionPayload }) => updateDialysisAdmission(id, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['dialysis'] }),
  });
};

export const useDialysisPrescription = (admissionId?: string) => useQuery({
  queryKey: ['dialysis', 'prescription', admissionId],
  queryFn: () => fetchDialysisPrescription(admissionId!),
  enabled: Boolean(admissionId),
  retry: (failureCount, error) => error instanceof ApiError && error.status === 404 ? false : failureCount < 2,
});

export const useSaveDialysisPrescription = (admissionId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertDialysisPrescriptionPayload) => saveDialysisPrescription(admissionId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['dialysis'] }),
  });
};
